import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';

import { requireMFASetup } from '../../utils/helper';
import { requireMFAValidation } from '../../utils/helper';
import { CognitoUser } from '../../types/user';

import { useClient } from './useClient';

type AuthStates = {
  status:
    | 'authenticated'
    | 'notMFAValidated'
    | 'requireMFASetup'
    | 'unauthenticated'
    | null;
  user: CognitoUser | null;
};

export function useAuthenticatedUser({
  checkMFAValidation = false,
  bypassCache = false,
}) {
  const { clientId, mfaToken } = useClient();
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthStates>({
    status: null,
    user: null,
  });

  useEffect(() => {
    const setAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser({ bypassCache });
        if (checkMFAValidation) {
          if (requireMFASetup(user))
            setAuthenticatedUser({ status: 'requireMFASetup', user: null });
          else if (await requireMFAValidation(user, mfaToken, clientId))
            setAuthenticatedUser({ status: 'notMFAValidated', user: null });
          else setAuthenticatedUser({ status: 'authenticated', user });
        } else setAuthenticatedUser({ status: 'authenticated', user });
      } catch (e) {
        setAuthenticatedUser({ status: 'unauthenticated', user: null });
      }
    };

    if (clientId) {
      setAuth();
    }
  }, [clientId, checkMFAValidation, bypassCache, mfaToken]);

  const refreshAuthenticatedUser = async () => {
    let user;
    try {
      user = await Auth.currentAuthenticatedUser();
      setAuthenticatedUser({ status: 'authenticated', user });
    } catch (e) {}
    return user;
  };

  return {
    authenticationStatus: authenticatedUser.status,
    authenticatedUser: authenticatedUser?.user
      ? {
          ...authenticatedUser?.user,
          attributes: {
            ...(authenticatedUser?.user?.attributes || {}),
            given_name: authenticatedUser?.user?.attributes?.given_name || '',
            family_name: authenticatedUser?.user?.attributes?.family_name || '',
          },
        }
      : authenticatedUser?.user,
    refreshAuthenticatedUser,
    setAuthenticatedUser,
  } as const;
}
